/**
 * LabAide tube-type lookup helper.
 *
 * Calls our server-side proxy (api/labaide-query.js) to resolve collection
 * requirements for a test, and extracts a compact tube-type string for table
 * display. Full bullets are returned too (for a tooltip / details view).
 *
 * LabAide v1 returns prose bullets, not structured fields, so we extract the
 * tube line heuristically. We keep the raw bullets so the UI can always fall
 * back to showing the full answer if extraction is uncertain.
 *
 * A module-level cache (by normalized test name) means each unique test is
 * fetched at most once per session — still worthwhile since LabAide enforces a
 * shared ~80 req/min limit across our single API key.
 */

const cache = new Map(); // normalizedName -> Promise<result> | result

const norm = (s) => (s || '').toString().trim().toLowerCase();
const stripMd = (s) => (s || '').replace(/\*\*/g, '').trim();

/**
 * Pull a tube-type string out of LabAide's prose bullets.
 * Returns the tube text, or null when no tube line is identifiable.
 */
export function extractTubeFromBullets(bullets) {
  if (!Array.isArray(bullets)) return null;

  // 1) Explicit "Tube: ..." line (the common case).
  for (const b of bullets) {
    const m = stripMd(b).match(/^tube\s*:?\s*(.+)$/i);
    if (m && m[1]) return m[1].trim();
  }

  // 2) Fallback: a line mentioning a tube / top color, minus its label.
  for (const b of bullets) {
    const c = stripMd(b);
    if (/^(code|minimum volume|invert|specimen|collection)\b/i.test(c)) continue;
    if (/\btube\b|\btop\b/i.test(c)) {
      return c.replace(/^[a-z /]+:\s*/i, '').trim();
    }
  }

  return null;
}

// Signature of an order's tests — used to invalidate the cached tube guide
// (submissions.tube_guide) when the ordered test set changes. Order-independent.
export function tubeGuideSig(tests) {
  return JSON.stringify(
    (Array.isArray(tests) ? tests : [])
      .map((t) => String((t && (t.id || t.code || t.name)) || '').trim())
      .filter(Boolean)
      .sort()
  );
}

// Shared fetch + cache for a single LabAide query `text`. Returns a normalized
// result and caches by the query string so repeats are free for the session.
function queryLabAide(text, { apiBase = '', source = 'admin-submission', force = false, cacheKey } = {}) {
  const key = cacheKey || norm(text);
  if (!key) return Promise.resolve({ status: 'error', error: 'Empty query' });
  if (force) cache.delete(key);
  if (cache.has(key)) return Promise.resolve(cache.get(key));

  const promise = (async () => {
    try {
      const res = await fetch(`${apiBase}/api/labaide-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, source }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.ok === false) {
        return {
          status: 'error',
          error: data.error || `Lookup failed (${res.status})`,
          bullets: data.bullets || [],
          _transient: res.status === 429 || res.status >= 500,
        };
      }
      if (data.requiresSourceSelection) {
        return { status: 'ambiguous', bullets: data.bullets || [] };
      }
      return {
        status: 'done',
        tube: extractTubeFromBullets(data.bullets),
        bullets: data.bullets || [],
      };
    } catch (e) {
      return { status: 'error', error: 'Network error', _transient: true };
    }
  })();

  cache.set(key, promise);
  promise.then((result) => {
    if (result.status === 'error' && result._transient) cache.delete(key);
    else cache.set(key, result);
  });
  return promise;
}

/**
 * Look up the tube type for a single test name via the proxy.
 * @returns {Promise<{status:'done'|'ambiguous'|'error', tube?:string|null, bullets?:string[], error?:string}>}
 */
export function lookupTubeType(testName, opts = {}) {
  if (!norm(testName)) return Promise.resolve({ status: 'error', error: 'No test name' });
  return queryLabAide(testName, opts);
}

/**
 * Look up collection requirements for ALL tests on a script in ONE request.
 * Sends a single combined query (so we get one consolidated response instead of
 * a separate API call per test) and returns one result with combined bullets.
 *
 * @param {Array<string | {name?: string, code?: string}>} tests
 *   Test names, or {name, code} objects. Codes are preferred for the query.
 */
export function lookupTubesForTests(tests, opts = {}) {
  const items = (Array.isArray(tests) ? tests : [])
    .map((t) => (typeof t === 'string' ? { name: t } : t || {}))
    .map((t) => ({
      name: (t.name || '').toString().trim(),
      code: (t.code || '').toString().trim(),
    }))
    .filter((t) => t.name || t.code);
  if (items.length === 0) return Promise.resolve({ status: 'error', error: 'No tests' });

  // Send a CLEAN, delimited list of identifiers — prefer the code (which has no
  // internal commas/parens) and fall back to the name. We deliberately do NOT
  // wrap this in a prose sentence: a phrase like "Collection requirements (tube,
  // specimen, volume) for: …" makes LabAide split on its OWN commas and parens
  // and echo the fragments back as bogus "Could not identify" warnings.
  const ids = items.map((t) => t.code || t.name);
  let text = ids.join(', ');
  if (text.length > 500) text = text.slice(0, 500);

  // Cache by the set of tests so the same script doesn't re-query.
  const cacheKey = 'multi:' + items.map((t) => norm(t.code || t.name)).sort().join('|');
  return queryLabAide(text, { ...opts, cacheKey });
}

// ── Structured tube-guide parsing ───────────────────────────────────────────
//
// LabAide returns the draw guide as a flat list of prose bullet lines: a summary
// ("11 tests → 2 tubes + 1 urine"), a "Draw Order" of tube lines (each "<emoji>
// <Nx> <Tube> (TYPE) — TEST (CODE), TEST (CODE) …" followed by its handling
// lines), an "Also Needed" group (urine / stool / swab), and ⚠ warnings.
//
// parseTubeGuide() turns that flat text into structured collection STEPS so the
// UI can render one checkable panel per tube instead of a wall of bullets.

// A leading emoji / symbol cluster, e.g. "🔴", "🧪", "⚙️", "✖️" (anything before
// the first word/space/paren character).
const leadingEmoji = (s) => {
  const m = (s || '').match(/^([^\w\s(]+)\s*/u);
  return m ? m[1].trim() : '';
};

// Strip markdown + any leading bullet/list marker ("•", "-", "*", "·").
const cleanLine = (raw) =>
  stripMd(String(raw || ''))
    .replace(/^[\s]*[••‣◦·*\-]+\s*/, '')
    .trim();

// A stable, render-independent key for a collection step (used to track which
// tubes the phleb has checked off). Derived from the tube label so it survives
// re-fetches of the same script.
const stepKeyFor = (label, collect) =>
  (label || collect || 'step')
    .toLowerCase()
    .replace(/\b\d+\s*x\b/g, '')
    .replace(/[^a-z0-9]+/g, '')
    .trim() || collect || 'step';

// Pull individual "NAME (CODE)" entries out of a tube's test list. Names can
// themselves contain commas (e.g. "FOLATE, RBC (467S)"), so we anchor on the
// trailing (CODE) rather than naively splitting on commas.
function parseTestList(right) {
  const tests = [];
  const re = /([^()]*?)\(([A-Za-z0-9]+)\)/g;
  let m;
  while ((m = re.exec(right))) {
    const name = m[1].replace(/^[\s,;]+/, '').trim();
    if (name) tests.push({ name, code: m[2] });
  }
  if (tests.length === 0) {
    // No codes present — fall back to a simple comma split.
    right
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((name) => tests.push({ name, code: '' }));
  }
  return tests;
}

// Decide whether a line introduces a collection step, and split it if so.
function splitStepLine(line) {
  const m = line.match(/^(.*?)\s[—–-]\s(.+)$/); // " — " / " – " / " - " (spaced)
  if (!m) return null;
  const left = m[1].trim();
  const right = m[2].trim();
  const hasCode = /\([A-Za-z0-9]+\)/.test(right);
  const tubeish = /(tube|top|urine|stool|swab|sst|edta|sodium|plasma|serum)/i.test(left);
  if (!hasCode && !tubeish) return null;
  return { left, right };
}

// Drop the bogus warnings LabAide produces when it misparses fragments: an
// unbalanced paren means it split a "NAME (CODE)" entry, and the denylist covers
// the query-scaffolding crumbs. Genuine warnings (e.g. "Volume … not in curated
// data") are kept.
function keepWarning(w) {
  const opens = (w.match(/\(/g) || []).length;
  const closes = (w.match(/\)/g) || []).length;
  if (opens !== closes) return false;
  const t = w.toLowerCase();
  if (/check spelling/.test(t) && t.length < 28) return false; // e.g. "Indices", "Plt)"
  if (
    /could not identify/.test(t) &&
    /(collection requirements|volume\) for|tube,|specimen|reflex|immunoassay|hydroxy|increment|sexually)/.test(t)
  )
    return false;
  return true;
}

/**
 * Parse LabAide's flat draw-guide bullets into structured steps.
 * @param {string[]} bullets
 * @returns {{ summary: string|null, steps: Array<{
 *   key:string, emoji:string, label:string, collect:'blood'|'urine'|'stool'|'swab',
 *   tests:Array<{name:string, code:string}>, handling:string[] }>, warnings:string[] }}
 */
export function parseTubeGuide(bullets) {
  const lines = (Array.isArray(bullets) ? bullets : [])
    .flatMap((b) => String(b || '').split('\n'))
    .map(cleanLine)
    .filter(Boolean);

  const steps = [];
  const warnings = [];
  const seen = new Map();
  let summary = null;
  let current = null;

  for (const line of lines) {
    // Warnings (⚠ / ⚠️).
    if (/^⚠/.test(line)) {
      const text = line.replace(/^⚠️?\s*/, '').trim();
      if (text && keepWarning(text)) warnings.push(text);
      continue;
    }

    const lower = line.toLowerCase();

    // Section headers / prompts we render ourselves — skip.
    if (lower === 'draw order' || lower === 'also needed' || lower.startsWith('want to add or remove')) {
      continue;
    }

    // Summary line ("🩸 11 tests → 2 tubes + 1 urine").
    if (!summary && (/^🩸/.test(line) || (/\btests?\b/.test(lower) && /(→|->|\btubes?\b)/.test(lower)))) {
      summary = line.replace(/^🩸\s*/, '').trim();
      continue;
    }

    // Collection-step line (a tube, or a urine/stool/swab specimen).
    const split = splitStepLine(line);
    if (split) {
      const emoji = leadingEmoji(split.left);
      const label = split.left
        .slice(emoji.length)
        .replace(/^\s*\d+\s*x\s*/i, '') // drop a leading "1x"
        .trim();
      // Classify from the tube LABEL/emoji only — never the test list, since a
      // blood tube can hold a test named e.g. "WBC, STOOL".
      const hay = label.toLowerCase();
      const collect = emoji === '🧪' || /\burine\b/.test(hay)
        ? 'urine'
        : /\bstool\b/.test(hay)
        ? 'stool'
        : /\bswab\b/.test(hay)
        ? 'swab'
        : 'blood';

      let key = stepKeyFor(label, collect);
      const n = (seen.get(key) || 0) + 1;
      seen.set(key, n);
      if (n > 1) key = `${key}-${n}`;

      current = {
        key,
        emoji: emoji || (collect === 'blood' ? '🩸' : '🧪'),
        label: label || (collect === 'urine' ? 'Urine' : 'Specimen'),
        collect,
        tests: parseTestList(split.right),
        handling: [],
      };
      steps.push(current);
      continue;
    }

    // Anything else is a handling instruction for the current step
    // (e.g. "Clot 30 min, invert 5x", "⚙️ Centrifuge after 30-min clot").
    if (current) current.handling.push(line);
  }

  return { summary, steps, warnings };
}
